
import axios from 'axios';

import type { PropType } from 'vue';

import {
  ref,
  defineComponent,
  defineAsyncComponent,
} from 'vue';

import { useI18n } from 'vue-i18n';

import { useToast } from 'primevue/usetoast';

import { TARGETING_TYPE_WHITE_LIST } from '@/libs/consts';

import type {
  GenericObject,
  EntriesPaginator,
  CampaignTargetingBySubids,
  Statistics,
} from '@/types';

import useEntriesNext from '@/composable/useEntries@next';

import useClipboard from '@/composable/useClipboard';

export default defineComponent({
  components: {
    Subid: defineAsyncComponent(() => import('@/components/Subid.vue')),
    ViewsColumn: defineAsyncComponent(() => import('@/components/Statistics/Advertiser/Columns/ViewsColumn.vue')),
  },
  props: {
    statisticsFilters: {
      type: Object as PropType<GenericObject>,
      required: true,
    },
    filters: {
      type: Object as PropType<GenericObject>,
      required: true,
    },
  },
  setup(props) {
    const i18n = useI18n();

    const toast = useToast();

    const {
      entries: statistics,
      entriesLoading: statisticsLoading,
      entriesFetched: statisticsFetched,
      fetchEntries: fetchStatistics,
    } = useEntriesNext<Statistics>('/api/statistics/advertiser/getStatisticsBySubids');

    const {
      entries: campaignTargetingBySubids,
      entriesFetched: campaignTargetingBySubidsFetched,
      fetchEntries: fetchCampaignTargetingBySubids,
    } = useEntriesNext<CampaignTargetingBySubids>('/api/campaign/getTargetingBySubidUUIDs');

    const { copyToClipboard } = useClipboard();

    const campaignId = props.statisticsFilters?.campaign_id.length === 1 ? props.statisticsFilters.campaign_id[0] : null;

    const fetchCampaignTargetingBySubidsWrapper = (): void => {
      if (!campaignId) {
        return;
      }

      fetchCampaignTargetingBySubids({
        campaign_id: campaignId,
      });
    };

    const fetchStatisticsWrapper = async (subidsPaginator: EntriesPaginator): Promise<[void, void]> => Promise.all([
      fetchStatistics({
        ...props.statisticsFilters,
        subids_paginator: subidsPaginator,
      }),
      fetchCampaignTargetingBySubidsWrapper(),
    ]);

    const subidUuidIsBlocked = (subidUuid: string): boolean => {
      if (!campaignTargetingBySubidsFetched.value) {
        return false;
      }

      const includes = campaignTargetingBySubids.subids_uuids.includes(subidUuid);

      return (campaignTargetingBySubids.targeting_type === TARGETING_TYPE_WHITE_LIST) !== includes;
    };

    const blockSubidUuidLoading = ref<Record<string, boolean>>({});

    const blockSubidUuid = async (subidUuid: string): Promise<void> => {
      blockSubidUuidLoading.value[subidUuid] = true;

      try {
        await axios.post(
          '/api/campaign/blockSubidsUUIDs',
          {
            campaigns_ids: [campaignId],
            subids_uuids: [subidUuid],
          },
        );

        toast.add({
          severity: 'success',
          summary: i18n.t('success'),
          detail: 'Subid blocked successfully.',
          life: 5000,
        });
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: i18n.t('error'),
          detail: i18n.t('unknown_error'),
          life: 5000,
        });
      }

      blockSubidUuidLoading.value[subidUuid] = false;

      if (campaignTargetingBySubids.targeting_type === TARGETING_TYPE_WHITE_LIST) {
        campaignTargetingBySubids.subids_uuids = campaignTargetingBySubids.subids_uuids.filter((uuid) => uuid !== subidUuid);
      } else {
        campaignTargetingBySubids.subids_uuids.push(subidUuid);
      }
    };

    const getStatisticRowClass = (data: GenericObject): string | null => (subidUuidIsBlocked(data.subid_uuid) ? 'statistics-table-row-subid-declined' : null);

    return {
      statistics,
      statisticsLoading,
      statisticsFetched,
      copyToClipboard,
      fetchStatisticsWrapper,
      campaignId,
      subidUuidIsBlocked,
      blockSubidUuidLoading,
      blockSubidUuid,
      getStatisticRowClass,
    };
  },
});
